import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import Layout from "./components/layout/Layout";
import Dashboard from "./pages/Dashboard";
import Programs from "./pages/Programs";
import Archives from "./pages/Archives";
import Sliders from "./pages/Sliders";
import FlyerTemplates from "./pages/FlyerTemplates";
import Countries from "./pages/Countries";
import Referrals from "./pages/Referrals";
import Sync from "./pages/Sync";
import Settings from "./pages/Settings";
import Login from "./pages/Login";
import Dealers from "./pages/Dealers";
import Customers from "./pages/Customers";
import Payouts from "./pages/Payouts";
import Commissions from "./pages/Commissions";
import EnergySavings from "./pages/EnergySavings";
import ConsultantRequests from "./pages/ConsultantRequests";
import AmazonClaims from "./pages/AmazonClaims";
import BulkDealerCheck from "./pages/BulkDealerCheck";
import BackofficeBackfill from "./pages/BackofficeBackfill";
import MonthlyReports from "./pages/MonthlyReports";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem("theme");
    return saved ? saved === "dark" : false; // Default to light theme
  });

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [isDark]);

  // Expose setIsDark to window for theme toggle
  React.useEffect(() => {
    (window as any).setTheme = setIsDark;
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />

            {/* Protected routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/programs"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Programs />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/archives"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Archives />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sliders"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Sliders />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/flyer-templates"
              element={
                <ProtectedRoute>
                  <Layout>
                    <FlyerTemplates />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/countries"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Countries />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/referrals"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Referrals />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sync"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Sync />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Settings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dealers"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dealers />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dealers/bulk-check"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BulkDealerCheck />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/customers"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Customers />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payouts"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Payouts />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/commissions"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Commissions />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/monthly-reports"
              element={
                <ProtectedRoute>
                  <Layout>
                    <MonthlyReports />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/energy-savings"
              element={
                <ProtectedRoute>
                  <Layout>
                    <EnergySavings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/consultant-requests"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ConsultantRequests />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/amazon-claims"
              element={
                <ProtectedRoute>
                  <Layout>
                    <AmazonClaims />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/backfill"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BackofficeBackfill />
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
        </Router>

        {/* Toast notifications */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: isDark ? "#374151" : "#363636",
              color: "#fff",
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: "#10B981",
                secondary: "#fff",
              },
            },
            error: {
              duration: 5000,
              iconTheme: {
                primary: "#EF4444",
                secondary: "#fff",
              },
            },
          }}
        />
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
